import React from 'react';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Form, DatePicker, Select, Row, Col, Input, Button as AntButton } from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SettingOutlined,
    DeleteOutlined,
    PlusCircleOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCan } from '@/ability';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import { ToolbarFilterButton } from '@/components/ui/ToolbarFilterButton';
import LeaveDetailPanel from '../components/LeaveDetailPanel';
import { useLeaves } from '../hooks/useLeaves';
import dayjs from 'dayjs';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    TenantToolbarSearchWrap,
    TenantToolbarSearchInput,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ErrorBlock,
} from '@/pages/inside/tenant/style';

const Leave = () => {
    const {
        total,
        annualBalance,
        leaveBalances,
        canManage,
        canApprove,
        activeView,
        setActiveView,
        user,
        leaveTypes,
        leaveTypeOptions,
        manageLeaveTypes,
        manageLeaveTypesPending,
        pendingCount,
        approvedCount,
        rejectedCount,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        panelOpen,
        closePanel,
        openPanel,
        selectedLeaveId,
        selectedLeaveEmployeeName,
        canCreate,
        canUpdate,
        createModalOpen,
        setCreateModalOpen,
        createForm,
        onSubmitCreate,
        closeCreateModal,
        createLeavePending,
        leavesError,
        filterStart,
        setFilterStart,
        filterEnd,
        setFilterEnd,
        filterType,
        setFilterType,
        filterStatus,
        setFilterStatus,
        filterModalOpen,
        setFilterModalOpen,
        filterStatusOptions,
        activeFilterCount,
        clearFilters,
        leaveStatusConfirm,
        leaveRecordForStatusConfirm,
        openLeaveApproveConfirm,
        openLeaveRejectConfirm,
        closeLeaveStatusConfirm,
        confirmLeaveStatusAction,
        leaveStatusConfirmPending,
        formatDate,
        capFirst,
    } = useLeaves();

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromAttendance = searchParams.get('from') === 'attendance';
    const isTenantRoute = window.location.pathname.startsWith('/tenant/');
    const canViewAllEmployees = useCan('hr.employees.view_all');
    const canViewAllAttendance = useCan('hr.attendance.view_all');
    const hasAnyHrViewAll = canViewAllEmployees || canViewAllAttendance;
    const showBackButton = fromAttendance || !hasAnyHrViewAll;

    const [statusForm] = Form.useForm();
    const [configForm] = Form.useForm();
    const [configModalOpen, setConfigModalOpen] = React.useState(false);
    const [balanceModalOpen, setBalanceModalOpen] = React.useState(false);

    const totalBalance = React.useMemo(() => {
        if (!leaveBalances || !Array.isArray(leaveBalances)) return 0;
        return leaveBalances.reduce((sum, b) => sum + (b.balance ?? 0), 0);
    }, [leaveBalances]);

    const kpis = React.useMemo(() => {
        const baseKpis = [
            {
                label: 'Total Leaves',
                value: total ?? 0,
                icon: <CalendarOutlined style={{ color: 'var(--ant-color-primary, #1890ff)' }} />,
                trend: 'all time',
                trendPositive: true,
            },
            {
                label: 'Pending',
                value: pendingCount ?? 0,
                icon: <ClockCircleOutlined style={{ color: '#faad14' }} />,
                trend: 'awaiting approval',
                trendPositive: true,
            },
            {
                label: 'Approved',
                value: approvedCount ?? 0,
                icon: <CheckCircleOutlined style={{ color: '#10b981' }} />,
                trend: 'approved',
                trendPositive: true,
            },
            {
                label: 'Rejected',
                value: rejectedCount ?? 0,
                icon: <CloseCircleOutlined style={{ color: '#ef4444' }} />,
                trend: 'rejected',
                trendPositive: false,
            },
        ];

        if (activeView === 'my') {
            return [
                {
                    label: 'Balance Leaves',
                    value: totalBalance ?? 0,
                    icon: <CalendarOutlined style={{ color: '#8b5cf6' }} />,
                    trend: 'view details',
                    trendPositive: true,
                    onClick: () => setBalanceModalOpen(true),
                },
                ...baseKpis
            ];
        }

        return baseKpis;
    }, [activeView, total, pendingCount, approvedCount, rejectedCount, totalBalance]);

    React.useEffect(() => {
        if (configModalOpen && leaveTypes) {
            configForm.setFieldsValue({
                leaveTypes: leaveTypes.map(t => ({ id: t.id, name: t.name, allowedCount: t.allowedCount }))
            });
        }
    }, [configModalOpen, leaveTypes, configForm]);

    React.useEffect(() => {
        if (leaveStatusConfirm && leaveRecordForStatusConfirm) {
            statusForm.setFieldsValue({
                startDate: leaveRecordForStatusConfirm.startDate ? dayjs(leaveRecordForStatusConfirm.startDate) : null,
                endDate: leaveRecordForStatusConfirm.endDate ? dayjs(leaveRecordForStatusConfirm.endDate) : null,
                remarks: '',
            });
        } else {
            statusForm.resetFields();
        }
    }, [leaveStatusConfirm, leaveRecordForStatusConfirm, statusForm]);

    const handleStatusFormSubmit = () => {
        statusForm.validateFields().then((values) => {
            confirmLeaveStatusAction(values);
        });
    };

    const createStartDate = Form.useWatch('startDate', createForm);
    const createEndDate = Form.useWatch('endDate', createForm);

    return (
        <ListBodyLayout
            title={
                showBackButton ? (
                    <span style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <AntButton
                            type="text"
                            shape="circle"
                            icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
                            onClick={() => navigate(isTenantRoute ? '/tenant/hr/employees' : '/hr/employees')}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}
                        />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span>Leave</span>
                            <span style={{ fontSize: 14, fontWeight: 400, opacity: 0.75 }}>Manage leave requests</span>
                        </span>
                    </span>
                ) : (
                    "Leave"
                )
            }
            description={showBackButton ? null : "Manage leave requests"}
            actions={
                <Container $display="flex" $gap="8px" style={{ width: 'auto' }}>
                    {activeView === 'employee' && canManage && (
                        <Button
                            $primary={false}
                            icon={<SettingOutlined />}
                            onClick={() => setConfigModalOpen(true)}
                            $padding="10px 20px"
                            $size="14px"
                        >
                            Configure Leaves
                        </Button>
                    )}
                    {activeView === 'my' && canCreate && (
                        <Button
                            $primary
                            $blackOnLight
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalOpen(true)}
                            $padding="10px 20px"
                            $size="14px"
                        >
                            Request Leave
                        </Button>
                    )}
                </Container>
            }
            kpiCards={kpis}
        >

            {leavesError && (
                <ErrorBlock>
                    Unable to load leave requests. Check that the HR API (GET /api/v1/hr/leave) is available.
                </ErrorBlock>
            )}

            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="24px"
            >
                <SliderTab
                    items={[
                        { key: STATUS_TAB.ALL, label: 'All' },
                        { key: STATUS_TAB.PENDING, label: 'Pending' },
                        { key: STATUS_TAB.APPROVED, label: 'Approved' },
                        { key: STATUS_TAB.REJECTED, label: 'Rejected' },
                    ]}
                    activeKey={statusTab}
                    onChange={setStatusTab}
                    fitContent
                />
                <Container $display="flex" $alignItems="center" $gap="8px" style={{ flexShrink: 0 }}>
                    <TenantToolbarSearchWrap>
                        <span className="tenant-toolbar-search-icon" aria-hidden>
                            <SearchOutlined />
                        </span>
                        <TenantToolbarSearchInput
                            type="search"
                            placeholder="Search"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            aria-label="Search leaves"
                        />
                    </TenantToolbarSearchWrap>
                    <ToolbarFilterButton
                        title="Filter leaves"
                        open={filterModalOpen}
                        onOpenChange={setFilterModalOpen}
                        hasActiveFilters={activeFilterCount > 0}
                        onClearApplied={clearFilters}
                        onApply={() => setFilterModalOpen(false)}
                        onClearDraft={clearFilters}
                    >
                        <Form layout="vertical" style={{ width: '100%', padding: '0 8px' }}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Form.Item label="Start date" style={{ marginBottom: 0 }}>
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="YYYY-MM-DD"
                                            value={filterStart}
                                            onChange={(val) => setFilterStart(val)}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="End date" style={{ marginBottom: 0 }}>
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="YYYY-MM-DD"
                                            value={filterEnd}
                                            onChange={(val) => setFilterEnd(val)}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Type" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="All types"
                                            options={leaveTypeOptions}
                                            value={filterType}
                                            onChange={(val) => setFilterType(val)}
                                            allowClear
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Status" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="All statuses"
                                            options={filterStatusOptions}
                                            value={filterStatus}
                                            onChange={(val) => setFilterStatus(val)}
                                            allowClear
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </ToolbarFilterButton>
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
                onRow={(record) => ({
                    onClick: () => openPanel(record.id),
                    style: { cursor: 'pointer' },
                })}
            />

            <AppModal
                size="md"
                title={selectedLeaveEmployeeName}
                open={panelOpen}
                onCancel={closePanel}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <LeaveDetailPanel
                    leaveId={selectedLeaveId}
                    onApproveRequested={openLeaveApproveConfirm}
                    onRejectRequested={openLeaveRejectConfirm}
                />
            </AppModal>

             <AppModal
                size="sm"
                title={leaveStatusConfirm === 'approve' ? 'Approve leave request' : 'Reject leave request'}
                open={!!leaveStatusConfirm}
                onCancel={closeLeaveStatusConfirm}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
                destroyOnHidden
            >
                <CreateRoleModalBody>
                    {leaveRecordForStatusConfirm && (
                        <Form form={statusForm} layout="vertical" onFinish={handleStatusFormSubmit}>
                            <p style={{ marginBottom: 16 }}>
                                {leaveStatusConfirm === 'approve' ? (
                                    <>
                                        Approve leave for <strong>{leaveRecordForStatusConfirm.userName}</strong> (
                                        {leaveRecordForStatusConfirm.userEmail})?
                                    </>
                                ) : (
                                    <>
                                        Reject leave for <strong>{leaveRecordForStatusConfirm.userName}</strong> (
                                        {leaveRecordForStatusConfirm.userEmail})?
                                    </>
                                )}
                            </p>
                            
                            {leaveStatusConfirm === 'approve' ? (
                                <>
                                    <Form.Item
                                        name="startDate"
                                        label="Approved Start Date"
                                        rules={[{ required: true, message: 'Select start date' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                                    </Form.Item>
                                    <Form.Item
                                        name="endDate"
                                        label="Approved End Date"
                                        rules={[{ required: true, message: 'Select end date' }]}
                                    >
                                        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                                    </Form.Item>
                                </>
                            ) : (
                                <Form.Item
                                    name="remarks"
                                    label="Optional Remarks / Reason for Rejection"
                                >
                                    <Input.TextArea placeholder="Enter rejection remarks..." rows={3} />
                                </Form.Item>
                            )}
                        </Form>
                    )}
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} onClick={closeLeaveStatusConfirm} $padding="10px 16px" $size="14px">
                        Cancel
                    </Button>
                    {leaveStatusConfirm === 'approve' ? (
                        <Button
                            $primary
                            $blackOnLight
                            onClick={handleStatusFormSubmit}
                            loading={leaveStatusConfirmPending}
                            permissionDenied={!canUpdate}
                            $padding="10px 16px"
                            $size="14px"
                        >
                            Approve
                        </Button>
                    ) : (
                        <Button
                            onClick={handleStatusFormSubmit}
                            loading={leaveStatusConfirmPending}
                            permissionDenied={!canUpdate}
                            $padding="10px 16px"
                            $size="14px"
                            style={{ backgroundColor: '#f5222d', border: 'none', color: '#fff' }}
                        >
                            Reject
                        </Button>
                    )}
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Create Leave"
                open={createModalOpen}
                onCancel={closeCreateModal}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={createForm} layout="vertical">
                        <Form.Item
                            name="startDate"
                            label="Start date"
                            rules={[{ required: true, message: 'Select start date' }]}
                        >
                            <DatePicker
                                format="YYYY-MM-DD"
                                onChange={(val) => {
                                    if (!val || !createEndDate) return;
                                    if (dayjs(createEndDate).isBefore(dayjs(val), 'day')) {
                                        createForm.setFieldsValue({ endDate: null });
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="endDate"
                            label="End date"
                            rules={[{ required: true, message: 'Select end date' }]}
                        >
                            <DatePicker
                                format="YYYY-MM-DD"
                                disabledDate={(current) => {
                                    if (!createStartDate || !current) return false;
                                    return dayjs(current).isBefore(dayjs(createStartDate), 'day');
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="type"
                            label="Type"
                            initialValue="annual"
                            rules={[{ required: true, message: 'Select type' }]}
                        >
                            <Select placeholder="Select type" options={leaveTypeOptions} />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} onClick={closeCreateModal} $padding="10px 16px" $size="14px">
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        onClick={onSubmitCreate}
                        loading={createLeavePending}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Create
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Configure Leave Types"
                open={configModalOpen}
                onCancel={() => setConfigModalOpen(false)}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form
                        form={configForm}
                        layout="vertical"
                        onFinish={(values) => {
                            const payload = {
                                leaveTypes: (values.leaveTypes || []).map(t => ({
                                    ...t,
                                    allowedCount: Number(t.allowedCount)
                                }))
                            };
                            manageLeaveTypes(
                                payload,
                                {
                                    onSuccess: () => {
                                        setConfigModalOpen(false);
                                    },
                                }
                            );
                        }}
                    >
                        <Form.List name="leaveTypes">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <div
                                            key={key}
                                            style={{
                                                display: 'flex',
                                                gap: 12,
                                                alignItems: 'center',
                                                marginBottom: 12,
                                            }}
                                        >
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'id']}
                                                hidden
                                            >
                                                <input type="hidden" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'name']}
                                                rules={[{ required: true, message: 'Missing type name' }]}
                                                style={{ flex: 2, marginBottom: 0 }}
                                            >
                                                <Input placeholder="Leave Type Name (e.g. sick)" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'allowedCount']}
                                                rules={[{ required: true, message: 'Missing limit' }]}
                                                style={{ flex: 1, marginBottom: 0 }}
                                            >
                                                <Input type="number" min={0} placeholder="Allowed Count" />
                                            </Form.Item>
                                            <Button
                                                onClick={() => remove(name)}
                                                $padding="6px"
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    color: '#ff4d4f',
                                                    cursor: 'pointer',
                                                    boxShadow: 'none',
                                                }}
                                            >
                                                <DeleteOutlined style={{ fontSize: 16 }} />
                                            </Button>
                                        </div>
                                    ))}
                                    <Button
                                        type="dashed"
                                        onClick={() => add({ name: '', allowedCount: 10 })}
                                        icon={<PlusCircleOutlined />}
                                        style={{ width: '100%', marginTop: 8 }}
                                    >
                                        Add Leave Type
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary={false}
                        onClick={() => setConfigModalOpen(false)}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        onClick={() => configForm.submit()}
                        loading={manageLeaveTypesPending}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Save Configurations
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Leave Balances Detail"
                open={balanceModalOpen}
                onCancel={() => setBalanceModalOpen(false)}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody style={{ padding: '8px 24px 24px 24px' }}>
                    <div style={{ marginBottom: 16, fontSize: '14px', color: 'var(--ant-color-text-secondary, #6b7280)' }}>
                        Detailed breakdown of your leave allowances and usage for the current calendar year.
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid var(--ant-color-border-secondary, #f0f0f0)' }}>
                                <th style={{ padding: '12px 8px', fontWeight: 600, color: 'var(--ant-color-text-heading, #111827)' }}>Type</th>
                                <th style={{ padding: '12px 8px', fontWeight: 600, color: 'var(--ant-color-text-heading, #111827)', textAlign: 'right' }}>Allowed (Total)</th>
                                <th style={{ padding: '12px 8px', fontWeight: 600, color: 'var(--ant-color-text-heading, #111827)', textAlign: 'right' }}>Used</th>
                                <th style={{ padding: '12px 8px', fontWeight: 600, color: 'var(--ant-color-text-heading, #111827)', textAlign: 'right' }}>Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaveBalances && leaveBalances.map((b, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid var(--ant-color-border-secondary, #f0f0f0)' }}>
                                    <td style={{ padding: '12px 8px', textTransform: 'capitalize', fontWeight: 500, color: 'var(--ant-color-text, #374151)' }}>{b.type}</td>
                                    <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--ant-color-text-secondary, #4b5563)' }}>{b.allowed}</td>
                                    <td style={{ padding: '12px 8px', textAlign: 'right', color: '#ef4444', fontWeight: 500 }}>{b.used}</td>
                                    <td style={{ padding: '12px 8px', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{b.balance}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary
                        $blackOnLight
                        onClick={() => setBalanceModalOpen(false)}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Close
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>
        </ListBodyLayout>
    );
};

export default Leave;
